import React, { ReactElement } from "react";
import Tooltip from "rc-tooltip";
import { css } from "@emotion/css";
import "rc-tooltip/assets/bootstrap.css";

let tooltipDescriptionSequence = 0;

export interface TooltipWrapperProps {
    text?: string | ReactElement;
    className?: string;
    place?: 'left' | 'right' | 'top' | 'bottom' | 'topLeft' | 'topRight' |
    'bottomLeft' | 'bottomRight' | 'rightTop' | 'rightBottom' | 'leftTop' | 'leftBottom' | string;
    replacementContentStyle?: string;
    mouseEnterDelay?: number;
    maxWidth?: number | string;
    trigger?: Array<'hover' | 'focus' | 'click'>;
    /** Always-mounted screen reader description for the tooltip's child. */
    accessibleDescription?: string;
}

const TooltipWrapper: React.FC<TooltipWrapperProps> = (props) => {
    const [descriptionId] = React.useState(() => `tooltip-description-${++tooltipDescriptionSequence}`);
    const child = props.accessibleDescription
        ? React.Children.only(props.children) as ReactElement
        : null;
    const getTooltipStyle = () => {
        const mw = props.maxWidth;
        const maxWidthValue = mw
            ? (typeof mw === 'number' ? `${mw}px` : mw)
            : undefined;

        return css`
            .rc-tooltip-inner {
                font-family: Muli;
                font-size: 14px;
                font-weight: normal;
                font-style: normal;
                letter-spacing: 0.6px;
                z-index: 120;
                color: white;
                background-color: #304562;
                box-shadow: -1px 4px 10px 0 rgba(48, 69, 98, 0.1);
                padding: 8px;
                min-height: 0;
                white-space: normal;
                word-wrap: break-word;
                ${maxWidthValue ? `max-width: ${maxWidthValue};` : ''}
            }
        `;
    };

    return (
        <>
            <Tooltip
                mouseEnterDelay={props.mouseEnterDelay ? props.mouseEnterDelay : 0.5}
                overlayClassName={
                    props.replacementContentStyle
                        ? props.replacementContentStyle
                        : getTooltipStyle() + " " + props.className
                }
                placement={props.place === undefined ? "top" : props.place}
                trigger={props.trigger || ["hover"]}
                overlay={
                    <span>{props.text}</span>
                }
            >
                {child ? React.cloneElement(child, {
                    "aria-describedby": [child.props["aria-describedby"], descriptionId].filter(Boolean).join(" ")
                }) : props.children}
            </Tooltip>
            {props.accessibleDescription && (
                <span id={descriptionId} className={accessibleDescriptionStyle}>
                    {props.accessibleDescription}
                </span>
            )}
        </>
    );
};

const accessibleDescriptionStyle = css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;

export default TooltipWrapper;
